import { getFlowsByPleasantnessValue, getHighestCountTuples } from "./WeekReviewHelper";
import { AnswerToWorkCategoryMap, WorkCategories } from "../../Constants";
import { getAnswerValues } from "../EntriesHelper";

/**
 * @param flows : An array of UI objects corresponding to flowentries.
 * @param pleasantnessValue: Default set to 'unpleasant' to get most recurring depleters at work. You can pass 'pleasant' to get recurring uplifters instead.
 * @returns An array of objects where each obj has a cause, count and topRecurringAnswers array (if any). 
 * If no unpleasant flows or work exploration causes found, it will return undefined
 */
export function getMostRecurringCausesAtWork(flows, pleasantnessValue = 'unpleasant') {
    // Get the unpleasant work flows
    const eligibleWorkFlows = getFlowsByPleasantnessValue(flows, 'work')[pleasantnessValue];
    // At least 2 eligible flows are needed for there to be a possibility of recurrence
    if (eligibleWorkFlows.length < 2) {
        return;
    }

    // Get causes from the 4 work categories ordered by most to least frequency of occurrence
    const workExplorationCauseCounts = getCauseCountInCategory(eligibleWorkFlows, WorkCategories.Exploration);
    if (workExplorationCauseCounts.length === 0) {
        return;
    }
    const maxCount = workExplorationCauseCounts[0][1];
    if (maxCount < 2) {
        // No recurrence possible if a cause didn't occur atleast twice
        return;
    }
    // Number for the main work exploration categories to be relevant
    const recurrenceThreshold = Math.max(2, Math.floor(0.75 * maxCount));

    // filter the workExplorationCauses that are above the threshold
    const qualifyingWorkCauses = workExplorationCauseCounts.filter(item => item[1] >= recurrenceThreshold);
    const recurrences = getEligibleRecurringAnswers(eligibleWorkFlows, qualifyingWorkCauses, recurrenceThreshold);
    const result = createOrderedListOfTopCauses(recurrences, workExplorationCauseCounts);

    return result;
}

/**
 * @param flows An array of UI objects corresponding to flowentries.
 * @param {string} category A category like 'work_cause_exploration', 'work_cause_people' etc. Aka categores where
 * the question asked under them should give the users the ability to select answers via radio or checkboxes
 * @returns An array or arrays where the child array is [answer, count]. The result is sorted in decending order of count by cause
 */
export function getCauseCountInCategory(flows, category) {
    let causeCounts = {};
    for (const flow of flows) {
        const question = flow.entry.answeredQuestions.find(question => question.category === category);
        if (!question?.answer) {
            continue;
        }

        if (question.answerFormat.includes('checkbox') || question.answerFormat.includes('radio')) {
            const answers = getAnswerValues(question.answer);
            answers.forEach(answer => {
                const key = cleanUpAnswer(answer);
                causeCounts[key] = (causeCounts[key] || 0) + 1;
            });
        }
        else {
            causeCounts[question.answer] = (causeCounts[question.answer] || 0) + 1;
        }
    }
    const causesSortedByCount = Object.entries(causeCounts).sort((a, b) => b[1] - a[1]);
    return causesSortedByCount;
}

function cleanUpAnswer(input) {
    // remove extra trailing spaces
    input = input.trim();

    // Remove the something else: type of prefix (regex from gpt) if input matches
    const regex = /^\s*something else[\s.]*:\s*/i;
    return regex.test(input) ? input.replace(regex, '') : input;
}

/**
 * This is a helper function used to get most recurring depleting or uplifting causes
 * @param eligibleFlows Pass in the list of unpleasant OR pleasant work flows
 * @param qualifyingWorkCauses Counts for the work exploration causes that were above the recurrence threshold e.g. [['People', 3], ['The work itself', 2], ['Processes / Policies', 3]]
 * @param recurrenceThreshold The starting point for the number of times an answer needs to reccur to be relevant
 * @returns An array of objects like [{cause: 'People', count: 3, topRecurringAnswers: [['Manager', 3], ['Team mates', 3] ]}]
 */
function getEligibleRecurringAnswers(eligibleFlows, qualifyingWorkCauses, recurrenceThreshold) {
    let recurrences = [];
    for (const [cause, count] of qualifyingWorkCauses) {
        if (count < recurrenceThreshold) {
            // skip this iteration
            continue;
        }
        // else calculate the recurrences in that category
        const category = AnswerToWorkCategoryMap[cause];
        const answerCountsInCategory = getCauseCountInCategory(eligibleFlows, category);
        if (answerCountsInCategory.length === 0) {
            // if there were no detail answers under that category, skip this iteration
            continue;
        }
        const maxRecurrenceCount = answerCountsInCategory[0][1]; // Because this is sorted in descending order already
        if (maxRecurrenceCount < 2) {
            // there's no recurrence for this cause, so skip to next
            continue;
        }
        // else see what recurring answers we can show for the qualifying categories
        let topRecurringAnswers = [];
        while (topRecurringAnswers.length === 0 && recurrenceThreshold >= 2) {
            topRecurringAnswers = getHighestCountTuples(answerCountsInCategory, recurrenceThreshold);
            // console.log(topRecurringAnswers, recurrenceThreshold, childAnswersThreshold, category)
            if (topRecurringAnswers.length > 0) {
                recurrences.push({
                    cause: cause,
                    count: count,
                    topRecurringAnswers: topRecurringAnswers
                });
                break;
            }
            else {
                recurrenceThreshold--;
            }
        }
    }

    return recurrences;
}

/**
 * This is a helper function to create the result in the format it can be shown to users
 * @param recurrences The value returned by getEligibleRecurringAnswers()
 * @param workExplorationCauseCounts Counts for each of the main work causes like : [['People', 3], ['The work itself', 2], ['Processes / Policies', 3], ['My schedule', 1]]
 * @returns An array of objects e.g [{"cause":"People","count":4},{"cause":"The work itself","count":3,"topRecurringAnswers":[["Not fast enough",3]]}]
 */
function createOrderedListOfTopCauses(recurrences, workExplorationCauseCounts) {
    let result = [];
    // Most recurring work exploration causes that should be shown
    const topWorkExplorationCauseCounts = getHighestCountTuples(workExplorationCauseCounts);
    // Iterate over the top work exp answers and add any eligible recurrence under them to the result
    for (const [cause, count] of topWorkExplorationCauseCounts) {
        const recurrence = recurrences.find(element => element.cause === cause);
        if (recurrence) {
            result.push(recurrence);
        }
        else {
            result.push({ cause: cause, count: count })
        }
    }

    // Iterate over the recurrences and add any eligible ones that are still left
    recurrences.forEach(recurrence => {
        const matchingResult = result.find(element => element.cause === recurrence.cause);
        if (!matchingResult) {
            // if not found in result, add it now
            result.push(recurrence);
        }
    });

    // Sort result array by count, then by length of topRecurringAnswers
    result.sort((a, b) => {
        // Sort by count in descending order
        if (b.count !== a.count) {
            return b.count - a.count;
        }

        // If count is the same, sort by length of topRecurringAnswers in descending order
        const aLength = a.topRecurringAnswers ? a.topRecurringAnswers.length : 0;
        const bLength = b.topRecurringAnswers ? b.topRecurringAnswers.length : 0;

        return bLength - aLength;
    });

    return result;
}

/**
 * This function can be used to get the most recurring causes in categories like causes_beyond_work, feelings_exploration etc
 * @returns An array of objects like [{cause: 'abc', count: 3}, {cause: 'efg', count: 2}]. 
 * OR undefined if not enough flows or no causes/recurrence found
 */
export function getMostRecurringCausesInCategory(flows, pleasantnessValue = 'unpleasant', category) {
    const eligibleFlows = getFlowsByPleasantnessValue(flows)[pleasantnessValue];
    if (eligibleFlows.length < 2) {
        return;
    }

    const causeCounts = getCauseCountInCategory(eligibleFlows, category);
    if (causeCounts.length === 0) {
        return;
    }
    const maxCount = causeCounts[0][1];
    if (maxCount < 2) {
        // No recurrence possible if a cause didn't occur atleast twice
        return;
    }
    const recurrenceThreshold = Math.max(2, Math.floor(0.7 * maxCount)); // 70% of max count
    const recurrences = getEligibleRecurringAnswers(eligibleFlows, causeCounts, recurrenceThreshold);
    const result = createOrderedListOfTopCauses(recurrences, causeCounts);

    return result;
}